import { useMemo, useState } from 'react'
import { Blocks, Loader2, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDebounce } from '@/components/hooks/use-debounce'
import {
  useAddDealAddOn,
  useAddOnCatalog,
  useDealAddOns,
  useDeleteDealAddOn,
} from '@/features/customers/addon-hooks'
import { formatMoney } from '@/lib/format'
import type { AddOnCatalogItem, DealAddOn } from '@/types/addon'

/**
 * DealAddOnsCard is the add-on line editor on the deal detail page. Lines
 * picked from the price book default to the catalog's sell + install price;
 * the deal's value is recomputed server-side on every change (base value +
 * line totals) and flows to the finance ledger for won deals.
 */
export function DealAddOnsCard({
  dealId,
  boatType,
  baseValue,
}: {
  dealId: string
  boatType?: string // deal boat's hull type; filters the picker when set
  baseValue: number
}) {
  const { data: lines = [], isLoading } = useDealAddOns(dealId)
  const addMutation = useAddDealAddOn(dealId)
  const deleteMutation = useDeleteDealAddOn(dealId)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [deletingLine, setDeletingLine] = useState<Pick<DealAddOn, 'id' | 'name'> | null>(null)

  const addonsTotal = useMemo(() => lines.reduce((sum, l) => sum + l.line_total, 0), [lines])

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <Blocks className="h-4 w-4" /> Add-Ons
          </CardTitle>
          <CardDescription>
            Itemized extras on this deal. The deal value is the base price plus these lines.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Add-On
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : lines.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No add-ons yet — add lines from the price book or enter a custom line.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="w-20 text-right">Qty</TableHead>
                <TableHead className="w-28 text-right">Unit</TableHead>
                <TableHead className="w-28 text-right">Total</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell className="max-w-72">
                    <span className="block truncate font-medium">{line.name}</span>
                    {line.notes && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {line.notes}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground">{line.category || '—'}</span>
                  </TableCell>
                  <TableCell className="text-right">{line.quantity}</TableCell>
                  <TableCell className="text-right">{formatMoney(line.unit_price)}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMoney(line.line_total)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      aria-label="Remove add-on line"
                      disabled={deleteMutation.isPending}
                      onClick={() => setDeletingLine({ id: line.id, name: line.name })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={4} className="text-right text-sm text-muted-foreground">
                  Add-ons total
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatMoney(addonsTotal)}
                </TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} className="text-right text-sm text-muted-foreground">
                  Base price + add-ons
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatMoney(baseValue + addonsTotal)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>

      <AddOnPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        boatType={boatType}
        isPending={addMutation.isPending}
        addError={addMutation.error instanceof Error ? addMutation.error.message : null}
        onPick={(item) => {
          addMutation.mutate(
            { catalog_item_id: item.id },
            { onSuccess: () => setPickerOpen(false) },
          )
        }}
        onAddCustom={(name, unitPrice) => {
          addMutation.mutate(
            { name, unit_price: unitPrice, quantity: 1 },
            { onSuccess: () => setPickerOpen(false) },
          )
        }}
      />

      {/* Line delete confirmation: removing a line re-rolls the deal value,
          so make the consequence explicit. */}
      <Dialog open={!!deletingLine} onOpenChange={(o) => !o && setDeletingLine(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove add-on?</DialogTitle>
            <DialogDescription>
              “{deletingLine?.name}” is removed from this deal and its value rolls back to{' '}
              {formatMoney(
                // Computed from the lines query alone (base + their sum −
                // this line), so the preview can't mix two refetch timelines.
                baseValue +
                  addonsTotal -
                  (deletingLine
                    ? (lines.find((l) => l.id === deletingLine.id)?.line_total ?? 0)
                    : 0),
              )}
              .
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingLine(null)}>
              Keep Line
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (!deletingLine) return
                deleteMutation.mutate(deletingLine.id, { onSettled: () => setDeletingLine(null) })
              }}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove Line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// --- Price book picker ---

interface AddOnPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Deal boat's hull type — defaults the category filter to what fits. */
  boatType?: string
  onPick: (item: AddOnCatalogItem) => void
  onAddCustom: (name: string, unitPrice: number) => void
  isPending: boolean
  addError: string | null
}

function AddOnPickerDialog({
  open,
  onOpenChange,
  boatType,
  onPick,
  onAddCustom,
  isPending,
  addError,
}: AddOnPickerDialogProps) {
  const [search, setSearch] = useState('')
  const [customMode, setCustomMode] = useState(false)
  const [customName, setCustomName] = useState('')
  const [customPrice, setCustomPrice] = useState('')
  const [category, setCategory] = useState<string>('all')
  const debounced = useDebounce(search, 300)

  const { data: items = [], isLoading } = useAddOnCatalog(
    open && !customMode
      ? { q: debounced || undefined, boat_type: boatType, active: true }
      : undefined,
  )

  const categories = useMemo(
    () => Array.from(new Set(items.map((i) => i.category))).sort(),
    [items],
  )
  const filtered = category === 'all' ? items : items.filter((i) => i.category === category)

  const reset = () => {
    setSearch('')
    setCustomMode(false)
    setCustomName('')
    setCustomPrice('')
    setCategory('all')
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add add-on</DialogTitle>
          <DialogDescription>
            {boatType
              ? 'Showing items that fit this boat, plus universal extras. '
              : 'Showing all catalog items. '}
            Pick from the price book, or add a custom line with your own price.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            placeholder="Search name or SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={customMode}
          />
          <Button variant="outline" onClick={() => setCustomMode(!customMode)}>
            {customMode ? 'Search catalog' : 'Custom line'}
          </Button>
        </div>

        {customMode ? (
          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="custom-addon-name">Name</Label>
              <Input
                id="custom-addon-name"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g. Trade-in credit accessory swap"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="custom-addon-price">Unit price ($)</Label>
              <Input
                id="custom-addon-price"
                type="number"
                min="0"
                step="0.01"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <DialogFooter>
              <Button
                disabled={!customName || isPending}
                onClick={() => onAddCustom(customName, Number(customPrice) || 0)}
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add line
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            {categories.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {['all', ...categories].map((c) => (
                  <Button
                    key={c}
                    size="sm"
                    variant={category === c ? 'default' : 'outline'}
                    className="h-7 px-2.5 text-xs"
                    onClick={() => setCategory(c)}
                  >
                    {c === 'all' ? 'All' : c}
                  </Button>
                ))}
              </div>
            )}
            <div className="max-h-80 overflow-y-auto rounded-md border">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No add-ons match. Use “Custom line” to add one manually.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="w-16" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-xs">{item.sku || '—'}</TableCell>
                        <TableCell className="max-w-64">
                          <span className="block truncate">{item.name}</span>
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {formatMoney(item.cost)}
                        </TableCell>
                        <TableCell className="text-right">{formatMoney(item.line_price)}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isPending}
                            onClick={() => onPick(item)}
                          >
                            Add
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </>
        )}
        {addError && <p className="text-sm text-destructive">{addError}</p>}
      </DialogContent>
    </Dialog>
  )
}
